import {
  ENUM_STEP_PROFILETYPE,
  ENUM_STEP_APPDOMAIN,
  ENUM_STEP_CREATIONSUCCESS,
  ENUM_STEP_DOCWARNING,
  ENUM_STEP_PROFILEINPUTS,
  ENUM_STEP_ERROR_409,
  ENUM_STEP_AZDOMAINS,
} from './formStepsEnum';

const customAppDomainId = 'customAppDomain';
const azDomainsId = 'azDomains';
const extraInputIds = [
  'emailDomain',
  'issuer',
  'openIdClientId',
  'acsUrl',
  'clientSecret',
  'mandatoryCertificate',
  'optionalCertificate',
];
const allExtraInputs = [azDomainsId, customAppDomainId, ...extraInputIds];

const profileTypeRequiresAzDomains = (currentProfileType) =>
  currentProfileType &&
  Object.prototype.hasOwnProperty.call(currentProfileType.editableFields, 'azDomains');

const profileTypeRequiresEarlyDoc = (currentProfileType) =>
  currentProfileType && currentProfileType.showEarlyDoc;

const profileTypeRequiresAppDomain = (currentProfileType) =>
  currentProfileType &&
  Object.prototype.hasOwnProperty.call(currentProfileType.editableFields, 'customAppDomain');

const profileTypeRequiresExtraInputs = (currentProfileType) =>
  currentProfileType &&
  Object.keys(currentProfileType.editableFields).some((element) => extraInputIds.includes(element));

const getAvailableSteps = (currentProfileType) =>
  [allExtraInputs].filter((el) =>
    Object.prototype.hasOwnProperty.call(currentProfileType.editableFields, el),
  );

export const errorInPreviousStep = (currentProfileType, errors) => {
  const profileInputs = getAvailableSteps(currentProfileType);

  return (
    (profileInputs.includes(customAppDomainId) && errors[customAppDomainId]) ||
    (profileInputs.includes(azDomainsId) && errors[azDomainsId]) ||
    false
  );
};

export const determineNextStep = (currentStep, currentProfileType) => {
  const requiresAzDomains = profileTypeRequiresAzDomains(currentProfileType);
  const requiresAppDomain = profileTypeRequiresAppDomain(currentProfileType);
  const requiresEarlyDoc = profileTypeRequiresEarlyDoc(currentProfileType);
  const requiresExtraInputs = profileTypeRequiresExtraInputs(currentProfileType);

  if (currentStep === ENUM_STEP_PROFILETYPE) {
    if (requiresEarlyDoc) {
      return ENUM_STEP_DOCWARNING;
    }

    if (requiresAzDomains) {
      return ENUM_STEP_AZDOMAINS;
    }

    if (requiresAppDomain) {
      return ENUM_STEP_APPDOMAIN;
    }

    if (requiresExtraInputs) {
      return ENUM_STEP_PROFILEINPUTS;
    }

    if (!requiresAppDomain && !requiresAzDomains && !requiresExtraInputs) {
      return ENUM_STEP_CREATIONSUCCESS;
    }
  }

  if (currentStep === ENUM_STEP_DOCWARNING) {
    if (requiresAzDomains) {
      return ENUM_STEP_AZDOMAINS;
    }

    if (requiresAppDomain) {
      return ENUM_STEP_APPDOMAIN;
    }

    if (requiresExtraInputs) {
      return ENUM_STEP_PROFILEINPUTS;
    }
  }

  if (currentStep === ENUM_STEP_APPDOMAIN) {
    if (requiresExtraInputs) {
      return ENUM_STEP_PROFILEINPUTS;
    }
  }

  return ENUM_STEP_CREATIONSUCCESS;
};

export const determinePreviousStep = (currentStep, currentProfileType) => {
  const requiresAzDomains = profileTypeRequiresAzDomains(currentProfileType);
  const requiresAppDomain = profileTypeRequiresAppDomain(currentProfileType);
  const requiresEarlyDoc = profileTypeRequiresEarlyDoc(currentProfileType);
  const requiresExtraInputs = profileTypeRequiresExtraInputs(currentProfileType);

  if (currentStep === ENUM_STEP_DOCWARNING) {
    return ENUM_STEP_PROFILETYPE;
  }

  if (currentStep === ENUM_STEP_AZDOMAINS) {
    if (requiresEarlyDoc) {
      return ENUM_STEP_DOCWARNING;
    }
    return ENUM_STEP_PROFILETYPE;
  }

  if (currentStep === ENUM_STEP_APPDOMAIN) {
    if (requiresAzDomains) {
      return ENUM_STEP_AZDOMAINS;
    }
    if (requiresEarlyDoc) {
      return ENUM_STEP_DOCWARNING;
    }
    return ENUM_STEP_PROFILETYPE;
  }

  if (currentStep === ENUM_STEP_PROFILEINPUTS) {
    if (requiresAppDomain) {
      return ENUM_STEP_APPDOMAIN;
    }

    if (requiresAzDomains) {
      return ENUM_STEP_AZDOMAINS;
    }

    return requiresEarlyDoc ? ENUM_STEP_DOCWARNING : ENUM_STEP_PROFILETYPE;
  }

  if (currentStep === ENUM_STEP_ERROR_409) {
    if (requiresExtraInputs) {
      return ENUM_STEP_PROFILEINPUTS;
    }
    if (requiresAppDomain) {
      return ENUM_STEP_APPDOMAIN;
    }

    if (requiresAzDomains) {
      return ENUM_STEP_AZDOMAINS;
    }

    return ENUM_STEP_PROFILETYPE;
  }

  throw new Error('Invalid step value');
};

export const isLastStep = (currentStep, currentProfileType) => {
  const requiresAzDomains = profileTypeRequiresAzDomains(currentProfileType);
  const requiresAppDomain = profileTypeRequiresAppDomain(currentProfileType);
  const requiresEarlyDoc = profileTypeRequiresEarlyDoc(currentProfileType);
  const requiresExtraInputs = profileTypeRequiresExtraInputs(currentProfileType);

  if (currentStep === ENUM_STEP_PROFILETYPE) {
    return !requiresAzDomains && !requiresAppDomain && !requiresExtraInputs && !requiresEarlyDoc;
  }

  if (currentStep === ENUM_STEP_AZDOMAINS) {
    return !requiresAppDomain && !requiresExtraInputs;
  }

  if (currentStep === ENUM_STEP_DOCWARNING) {
    return !requiresAppDomain && !requiresExtraInputs;
  }

  if (currentStep === ENUM_STEP_APPDOMAIN) {
    return !requiresExtraInputs;
  }

  if (currentStep === ENUM_STEP_PROFILEINPUTS) {
    return true;
  }

  if (currentStep === ENUM_STEP_CREATIONSUCCESS) {
    return false;
  }

  if (currentStep === ENUM_STEP_ERROR_409) {
    return false;
  }

  throw new Error('Invalid step value');
};

export const isFirstProfileInput = (
  editableFields,
  inputId,
  skipCustomAppDomain,
  skipAzDomains,
) => {
  const profileTypeFields = Object.keys(editableFields);
  if (profileTypeFields.length < 0) {
    return undefined;
  }

  let filteredFields = profileTypeFields;
  if (skipCustomAppDomain) {
    filteredFields = filteredFields.filter((i) => i !== 'customAppDomain');
  }

  if (skipAzDomains) {
    filteredFields = filteredFields.filter((i) => i !== 'azDomains');
  }

  const orderedInputs = allExtraInputs.filter((i) => filteredFields.includes(i));
  return orderedInputs[0] === inputId;
};
export const isLastProfileInput = (editableFields, inputId) => {
  const profileTypeFields = Object.keys(editableFields);
  if (profileTypeFields.length < 0) {
    return undefined;
  }

  const orderedInputs = allExtraInputs.filter((i) => profileTypeFields.includes(i));
  return orderedInputs[orderedInputs.length - 1] === inputId;
};

export const isAForm = (currentStep) =>
  currentStep === ENUM_STEP_AZDOMAINS ||
  currentStep === ENUM_STEP_APPDOMAIN ||
  currentStep === ENUM_STEP_PROFILEINPUTS;
